import { Box, Flex, Text } from "@highoutput/ui-components";
import { forwardRef, useState } from "react";
import { InView } from "react-intersection-observer";
import { howItWorks } from "./data";

export default function HowItWorks() {
  const [shouldStart, setShouldStart] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <Flex
      direction="column"
      maxWidth={"1440px"}
      mx="auto"
      bgColor="white"
      py={{ lg: "100px", base: "64px" }}
      px={{ xl: "160px", lg: "24px", base: "16px" }}
    >
      <Flex
        direction={"column"}
        gap="16px"
        justify={"start"}
        marginRight={"auto"}
        pb={{ base: "56px", xl: "54px", lg: "48px" }}
      >
        <Text color="#0D3050" fontFamily={"Inter"}>
          HOW IT WORKS
        </Text>
        <Text
          color="#0D3050"
          fontFamily={"Georgia"}
          fontSize={{ base: "44px", lg: "80px" }}
          lineHeight={{ base: "48px", lg: "88px" }}
          letterSpacing={"-0.02em"}
        >
          Our approach
        </Text>
      </Flex>
      <InView
        threshold={0.15}
        onChange={(inView) => {
          if (inView && !shouldStart) setShouldStart(true);
          if (!inView && shouldStart) setShouldStart(false);
        }}
      >
        {(...args) => (
          <Flex
            ref={args[0].ref}
            id="how-it-works"
            height="full"
            data-testid="howitworks.items"
            direction="column"
            gap={{ base: "16px" }}
          >
            {howItWorks.map((data, index) => (
              <InView
                key={data.id}
                threshold={0.75}
                onChange={(inView) => {
                  if (!shouldStart) return;
                  if (!inView && activeIndex === index) return setActiveIndex(index + 1);
                  if (inView && activeIndex - 1 === index) setActiveIndex(index);
                }}
              >
                {({ ref }) => <Item ref={ref} data={data} isActive={activeIndex === index} />}
              </InView>
            ))}
          </Flex>
        )}
      </InView>
    </Flex>
  );
}

type ItemProps = {
  data: typeof howItWorks[number];
  isActive?: boolean;
};

const Item = forwardRef<HTMLDivElement, ItemProps>(function Item(props: ItemProps, ref) {
  const { id, title, description } = props.data;

  return (
    <Box
      ref={ref}
      data-testid={`howitworks.items.${id}`}
      transition="all"
      w="full"
      borderLeftWidth={"4px"}
      borderLeftStyle={"solid"}
      transitionDuration="300ms"
      {...(props.isActive && {
        borderLeftColor: "#F9D209",
        bgColor: "rgba(240, 237, 236, 0.2)",
        borderRadius: "0px 8px 8px 0px",
      })}
      {...(!props.isActive && {
        borderLeftColor: "transparent",
      })}
      py={{ lg: "24px", base: "24px" }}
      pl={{ xl: "22px", lg: "24px", base: "23px" }}
      pr={{ lg: "24px", base: "22px" }}
      fontFamily={"Inter"}
    >
      <Text
        variant={{
          base: "20/32",
          lg: "24/32",
          xl: "24/32",
        }}
        fontSize={"24px"}
        lineHeight={"32px"}
        data-testid={`howitworks.items.${id}.title`}
        fontWeight={600}
        color="#0D3050"
        mb={"8px"}
      >
        {title}
      </Text>

      <Text
        variant={{
          base: "16/24",
          lg: "20/32",
          xl: "20/32",
        }}
        letterSpacing={{ lg: "unset", base: "0.02em" }}
        color="rgba(13, 48, 80, 0.8)"
        data-testid={`howitworks.items.${id}.description`}
      >
        {description}
      </Text>
    </Box>
  );
});
